import { useSessionContext } from "@/contexts/SessionContext";
import { useViewContext } from "@/contexts/ViewContext";
import { useAppointmentActions } from "@/components/schedule/hooks/useAppointmentActions";
import {
  AttachMoney,
  Check,
  Close,
  Description,
  PlayArrow,
  Key,
  WhatsApp,
  Notifications,
  EditCalendar,
} from "@mui/icons-material";
import { Button, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import { VerifyResponsibleDialog } from "./VerifyResponsibleDialog";
import { WarningAppointment } from "@/pages/schedule/components/WarningAppointment";
import { GoogleCalendarButton } from "@/components/common/GoogleCalendarButton";
import { useContractContext } from "@/contexts/ContractContext";
import { PaymentPendingModal } from "../common/PaymentPendingModal";
import { ConfirmationPendingModal } from "../common/ConfirmationPendingModal";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useMemo, useState } from "react";
import ConfirmTokenDialog from "./components/ConfirmTokenDialog";
import { SendQuestionnairesTrigger } from "./components/SendQuestionnairesTrigger";
import ScheduleCancelDialog from "./ScheduleCancelDialog";
import ScheduleRescheduleDialog from "./ScheduleRescheduleDialog";
import { useDatetime } from "@/utils/useDatetime";
import { useFormatter } from "@/utils/useFormatter";
import { useInternationalVerify } from "@/utils/useInternationalVerify";
import { currentFeatures, oids } from "@/configs/Settings";
import dayjs from "dayjs";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import { resolveOptionalAppointmentBasedOn } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import type {
  MessagingAppointment,
  MessagingPatient,
  MessagingTemplate,
} from "src-next/modules/crm-clinical/messaging";
import { useOrganizationContext } from "@/contexts/OrganizationContext";

const getStatusColor = (status: string) => {
  const colors = {
    cancelled: "error",
    proposed: "default",
    pending: "warning",
    arrived: "success",
    booked: "primary",
  } as const;
  return colors[status as keyof typeof colors] || "default";
};

const getStatusLabel = (status: string) => {
  const labels = {
    cancelled: "Cancelado",
    proposed: "Proposta",
    pending: "Pendente",
    arrived: "Confirmado",
    booked: "Confirmado",
    fulfilled: "Finalizado",
  };
  return labels[status as keyof typeof labels] || status;
};

interface ScheduleQueueActionsProps {
  appointment: any;
  setRefetch: any;
  patient?: fhir5.BundleEntry<fhir5.Patient> | undefined;
  handleCancelAppointment: any;
  locationTeleconsulting: boolean;
  variant?: "compact" | "descriptive"; // Nova prop para controlar o estilo
  /**
   * Templates já filtrados e prontos para o dialog (buscados no ScheduleQueue).
   */
  messagingTemplates: MessagingTemplate[];
  /**
   * Dados do tutor no modo vet (passados por `ScheduleQueue`).
   * - phone: usado para "Abrir no WhatsApp Web"
   * - cpf: usado para envio via FluxZap
   */
  tutorPhone?: string;
  tutorCpf?: string;
}

export function ScheduleQueueActions({
  appointment,
  setRefetch,
  patient,
  handleCancelAppointment,
  locationTeleconsulting,
  variant = "compact", // Valor padrão é compact para manter compatibilidade
  messagingTemplates,
  tutorPhone,
  tutorCpf,
}: ScheduleQueueActionsProps) {
  const { viewType } = useViewContext();
  const { organizationId } = useOrganizationContext();
  const { user } = useSessionContext();
  const { inProgress } = useClinicalRegisterContext();
  const { formatDateTimeToHour } = useFormatter;
  const { formatDate } = useDatetime;

  const { currentContract } = useContractContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan",
    );

  const phone = patient?.resource?.telecom?.find(
    (t) => t.system === "phone",
  )?.value;

  const isVet = !currentFeatures.defaultApp;
  const whatsappPhone = isVet ? tutorPhone : phone;
  const whatsappCpf = isVet ? tutorCpf : undefined;

  const [isSendWhatsAppDialogOpen, setIsSendWhatsAppDialogOpen] =
    useState(false);

  const messagingAppointment: MessagingAppointment = useMemo(() => {
    return {
      id: appointment?.id ?? appointment?.originalData?.id ?? "appointment",
      startAtIso:
        appointment?.startTime ??
        appointment?.start ??
        appointment?.originalData?.start ??
        undefined,
    };
  }, [appointment]);

  const messagingPatient: MessagingPatient | null = useMemo(() => {
    if (!patient?.resource?.id) return null;

    const patientNameObj = patient.resource?.name?.find(
      (n: any) => n.use === "usual" || n.use === "official",
    );
    const fullName = String(patientNameObj?.text ?? appointment?.patient ?? "")
      .trim()
      .slice(0, 200);

    if (isVet) {
      return {
        id: `tutor-of-${patient.resource.id}`,
        fullName: fullName ? `Tutor de ${fullName}` : "Tutor",
        cpf: whatsappCpf ? String(whatsappCpf) : undefined,
        phone: whatsappPhone ? String(whatsappPhone) : undefined,
        // Alinha com `useTutorLookup`: tasks/FluxZap usam Patient lógico do tutor (CPF).
        isTutor: true,
      };
    }

    // No modo humano, o envio real depende de CPF (quando o caller não injeta `forReference`).
    const cpfFromId =
      patient.resource.id && String(patient.resource.id).includes("-")
        ? String(patient.resource.id).split("-")[1]
        : "";

    return {
      id: patient.resource.id,
      fullName: fullName || "Paciente",
      cpf: cpfFromId || undefined,
      phone: whatsappPhone ? String(whatsappPhone) : undefined,
    };
  }, [
    appointment?.patient,
    isVet,
    patient?.resource?.id,
    whatsappCpf,
    whatsappPhone,
  ]);

  const lembreteMessage = locationTeleconsulting
    ? `Olá ${
        appointment.patient
      }. Você possui uma teleconsulta agendada para ${formatDate(
        appointment?.start || "",
      )} - ${formatDateTimeToHour(appointment?.start || "")} com Dr. ${
        appointment.practitioner
      } na especialidade de ${
        appointment.healthcare
      }. Para acessar a videoconferênciar por favor acesse o link: ${
        appointment?.virtualService?.[0]?.addressUrl
      }. Acesse https://app.fluxmed.com.br, e faça seu cadastro para acessar seus registros eletrônicos de saúde, agendar consultas e muito mais.`
    : `Olá ${
        appointment.patient
      }. Você possui uma consulta agendada para ${formatDate(
        appointment?.start || "",
      )} - ${formatDateTimeToHour(appointment?.start || "")} com Dr. ${
        appointment.practitioner
      } na especialidade de ${appointment.healthcare}. Endereço: ${
        appointment.address
      }. Acesse https://app.fluxmed.com.br, e faça seu cadastro para acessar seus registros eletrônicos de saúde, agendar consultas e muito mais.`;

  const {
    startServiceLoading,
    handleRemoveFromCalendar,
    handleStartService,
    handleAddToCalendar,
    loadingCalendar,
    handleViewRecords,
    verifyDialog,
    setVerifyDialog,
    fetchRelated,
    warningAction,
    showPaymentWarning,
    related,
    fetchPayment,
    setShowPaymentWarning,
    paymentModalOpen,
    setPaymentModalOpen,
    loadingSendCharge,
    errorSendCharge,
    loadingConfirmPayment,
    handleSendCharge,
    handleConfirmPayment,
    loadingConfirmProposed,
    handleConfirmProposedAppointment,
  } = useAppointmentActions({ appointment, setRefetch });

  const [isConfirmTokenDialogOpen, setIsConfirmTokenDialogOpen] =
    useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  /** Referência FHIR do paciente para tasks / envio de questionários (admin/workflow). */
  const patientReferenceForTasks = useMemo(() => {
    const fromPatientBundle = patient?.resource?.id
      ? `Patient/${patient.resource.id}`
      : null;
    const fromAppointment =
      typeof appointment?.subject?.reference === "string"
        ? appointment.subject.reference
        : null;
    return fromPatientBundle ?? fromAppointment ?? null;
  }, [appointment?.subject?.reference, patient?.resource?.id]);

  const hasActiveEncounterForQuestionnaires = useMemo(
    () =>
      Boolean(
        appointment?.id &&
        inProgress &&
        String(inProgress) === String(appointment.id),
      ),
    [appointment?.id, inProgress],
  );

  const appointmentReferenceForTaskSend = useMemo(
    () =>
      resolveOptionalAppointmentBasedOn(
        appointment?.originalData?.id ?? appointment?.id,
      ),
    [appointment?.id, appointment?.originalData?.id],
  );

  const hasCalendarId = appointment?.originalData?.extension?.some(
    (ext: any) => ext.url.includes("googleCalendarId") && ext.valueString,
  );

  // Verifica se o appointment já passou da data/hora de fim usando dayjs
  const isAppointmentExpired =
    dayjs(appointment.endTime).endOf("day").isBefore(dayjs()) ||
    dayjs(appointment?.originalData?.end)?.endOf("day").isBefore(dayjs());

  /**
   * Ícone de ação para pending/proposed:
   * - pending: sempre exibe (pagamento ou confirmação conforme FluxPay)
   * - proposed: só exibe se FluxPay estiver ativo
   */
  const pendingOrProposedActionButton =
    appointment?.status === "pending" ||
    (appointment?.status === "proposed" && hasFluxPayExtension) ? (
      hasFluxPayExtension ? (
        <Tooltip title="Pendente de pagamento">
          <IconButton
            sx={{ color: "primary800.main" }}
            onClick={() => {
              setPaymentModalOpen(true);
            }}
          >
            <AttachMoney />
            {appointment?.originalData?.extension?.find(
              (ext: any) =>
                ext.url ===
                  "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent" &&
                ext.valueBoolean,
            ) && (
              <Check sx={{ fontSize: 16, color: "success700.main" }} />
            )}
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Pendente de confirmação">
          <IconButton
            sx={{ color: "primary800.main" }}
            onClick={() => {
              setConfirmationModalOpen(true);
            }}
          >
            <AttachMoney />
          </IconButton>
        </Tooltip>
      )
    ) : null;

  return (
    <>
      <Stack spacing={1} alignItems="flex-end">
        {variant === "descriptive" ? (
          // Layout para modal - todos os botões em uma linha
          <Stack
            direction="column"
            spacing={2}
            sx={{
              width: "100%",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                flexWrap: "wrap",
                gap: 1,
                width: "100%",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {viewType === "user" && user?.refreshTokenGoogle && (
                  <GoogleCalendarButton
                    isInCalendar={!!hasCalendarId}
                    onAddToCalendar={() => {
                      handleAddToCalendar?.(appointment);
                    }}
                    onRemoveFromCalendar={() => {
                      handleRemoveFromCalendar(appointment);
                    }}
                    disabled={loadingCalendar}
                    loading={loadingCalendar}
                  />
                )}

                {pendingOrProposedActionButton}

                {whatsappPhone && (
                  <>
                    <Tooltip title="Enviar WhatsApp">
                      <IconButton
                        sx={{ color: "success.main" }}
                        onClick={() => setIsSendWhatsAppDialogOpen(true)}
                      >
                        <WhatsApp fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Enviar lembrete">
                      <IconButton
                        color="secondary"
                        onClick={() =>
                          window.open(
                            `https://web.whatsapp.com/send?phone=+${useInternationalVerify.verifyPhoneDDI(
                              whatsappPhone,
                            )}&text=${lembreteMessage}`,
                            "_blank",
                          )
                        }
                      >
                        <Notifications fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                {viewType === "user" && (
                  <SendQuestionnairesTrigger
                    patientReference={patientReferenceForTasks}
                    variant="descriptive"
                    appointmentStatus={appointment?.status}
                    appointmentReference={appointmentReferenceForTaskSend}
                    hasActiveEncounter={hasActiveEncounterForQuestionnaires}
                  />
                )}

                {/* Token para Plano de Saúde */}
                {(appointment?.supportingInfo ||
                  appointment?.supportingInformation) &&
                  appointment?.status !== "booked" && (
                    <Tooltip title="Gerar token para plano de saúde">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsConfirmTokenDialogOpen(true);
                        }}
                        sx={{ color: "info.main" }}
                      >
                        <Key fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                <Chip
                  label={getStatusLabel(appointment.status)}
                  color={getStatusColor(appointment.status)}
                  sx={{ minWidth: 90 }}
                />
              </Stack>

              {appointment.status !== "cancelled" &&
                appointment.status !== "fulfilled" && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flexWrap: "wrap", gap: 1 }}
                  >
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<EditCalendar />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRescheduleDialogOpen(true);
                      }}
                    >
                      Reagendar Consulta
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelAppointment(appointment?.originalData);
                      }}
                    >
                      Cancelar
                    </Button>
                  </Stack>
                )}
            </Stack>

            {/* Botão de confirmação para appointments "proposed" - abre remarcação */}
            {appointment.status === "proposed" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<EditCalendar />}
                onClick={async (e) => {
                  e.stopPropagation();
                  setRescheduleDialogOpen(true);
                }}
              >
                Confirmar e Definir Horário
              </Button>
            )}

            {/* Botões principais com texto */}
            {viewType === "user" && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1, width: "100%" }}
              >
                {!isAppointmentExpired && appointment.status === "booked" && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrow />}
                    onClick={async (e) => {
                      e.stopPropagation();
                      handleStartService(appointment);
                    }}
                    disabled={startServiceLoading}
                    fullWidth
                  >
                    Iniciar Atendimento
                  </Button>
                )}

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Description />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewRecords(appointment?.originalData);
                  }}
                  fullWidth
                >
                  Ver Registros
                </Button>
              </Stack>
            )}
          </Stack>
        ) : (
          // Layout original para lista - dois Stacks separados
          <>
            <Stack direction="row" spacing={2} alignItems="center">
              {viewType === "user" && user?.refreshTokenGoogle && (
                <GoogleCalendarButton
                  isInCalendar={!!hasCalendarId}
                  onAddToCalendar={() => {
                    handleAddToCalendar?.(appointment);
                  }}
                  onRemoveFromCalendar={() => {
                    handleRemoveFromCalendar(appointment);
                  }}
                  disabled={loadingCalendar}
                  loading={loadingCalendar}
                />
              )}

              {pendingOrProposedActionButton}

              {whatsappPhone && (
                <>
                  <Tooltip title="Enviar WhatsApp">
                    <IconButton
                      sx={{ color: "success.main" }}
                      onClick={() => setIsSendWhatsAppDialogOpen(true)}
                    >
                      <WhatsApp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Enviar lembrete">
                    <IconButton
                      color="secondary"
                      onClick={() =>
                        window.open(
                          `https://web.whatsapp.com/send?phone=+${useInternationalVerify.verifyPhoneDDI(
                            whatsappPhone,
                          )}&text=${lembreteMessage}`,
                          "_blank",
                        )
                      }
                    >
                      <Notifications fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <SendQuestionnairesTrigger
                patientReference={patientReferenceForTasks}
                variant="compact"
                appointmentStatus={appointment?.status}
                hasActiveEncounter={hasActiveEncounterForQuestionnaires}
              />

              {/* Token para Plano de Saúde */}
              {(appointment?.supportingInfo ||
                appointment?.supportingInformation) &&
                appointment?.status !== "booked" && (
                  <Tooltip title="Gerar token para plano de saúde">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsConfirmTokenDialogOpen(true);
                      }}
                      sx={{ color: "info.main" }}
                    >
                      <Key fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

              <Chip
                label={getStatusLabel(appointment.status)}
                color={getStatusColor(appointment.status)}
                sx={{ minWidth: 90 }}
              />
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
              sx={{
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              {/* Botão de confirmação para appointments "proposed" - abre remarcação */}
              {appointment.status === "proposed" && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<EditCalendar />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setRescheduleDialogOpen(true);
                  }}
                >
                  Confirmar e Definir Horário
                </Button>
              )}

              {/* Botões principais com texto */}
              {viewType === "user" && (
                <>
                  {!isAppointmentExpired && appointment.status === "booked" && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrow />}
                      onClick={async (e) => {
                        e.stopPropagation();
                        handleStartService(appointment);
                      }}
                      disabled={startServiceLoading}
                    >
                      Iniciar Atendimento
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Description />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewRecords(appointment?.originalData);
                    }}
                  >
                    Ver Registros
                  </Button>
                </>
              )}

              {appointment.status !== "cancelled" &&
                appointment.status !== "fulfilled" && (
                  <>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<EditCalendar />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRescheduleDialogOpen(true);
                      }}
                    >
                      Reagendar Consulta
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelAppointment(appointment?.originalData);
                      }}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
            </Stack>
          </>
        )}
      </Stack>

      {paymentModalOpen && (
        <PaymentPendingModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          loadingSendCharge={loadingSendCharge}
          errorSendCharge={errorSendCharge}
          loadingConfirmPayment={loadingConfirmPayment}
          onSendCharge={handleSendCharge}
          onConfirmPayment={handleConfirmPayment}
          notificationSent={
            !!appointment?.extension?.find(
              (ext: any) =>
                ext.url ===
                  "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent" &&
                ext.valueBoolean,
            )
          }
        />
      )}

      {confirmationModalOpen && (
        <ConfirmationPendingModal
          isOpen={confirmationModalOpen}
          onClose={() => setConfirmationModalOpen(false)}
          loadingConfirm={
            appointment?.status === "proposed"
              ? loadingConfirmProposed
              : loadingConfirmPayment
          }
          onConfirm={async () => {
            if (appointment?.status === "proposed") {
              await handleConfirmProposedAppointment();
            } else {
              await handleConfirmPayment();
            }
          }}
        />
      )}

      {showPaymentWarning && (
        <WarningAppointment
          isOpen={showPaymentWarning}
          handleClose={() => setShowPaymentWarning(false)}
          onConfirm={() => {
            setShowPaymentWarning(false);

            if (warningAction === "navigate") {
              handleViewRecords(appointment?.originalData);
            } else if (warningAction === "startService") {
              handleStartService(appointment, true);
            }
          }}
          appointmentDescription="Esta consulta"
          appointment={appointment?.originalData}
          hasSubscriptionFluxPay={hasSubscriptionFluxPay}
          onPaymentSent={fetchPayment}
        />
      )}

      {isConfirmTokenDialogOpen && (
        <ConfirmTokenDialog
          onConfirm={handleConfirmPayment}
          isOpen={isConfirmTokenDialogOpen}
          handleClose={() => setIsConfirmTokenDialogOpen(false)}
        />
      )}

      {rescheduleDialogOpen && (
        <ScheduleRescheduleDialog
          isOpen={rescheduleDialogOpen}
          setIsOpen={setRescheduleDialogOpen}
          appointment={appointment?.originalData}
          setRefetch={setRefetch}
          payment={[]} // O diálogo busca o pagamento internamente se necessário
        />
      )}

      {verifyDialog && (
        <VerifyResponsibleDialog
          handleClose={() => setVerifyDialog(false)}
          isOpen={true}
          loading={false}
          setLoading={() => {}}
          dataAppointment={appointment?.originalData}
          related={related}
          fetchRelated={fetchRelated}
          patientId={appointment?.subject?.reference?.split("-")[1]}
        />
      )}

      {messagingPatient && (
        <SendWhatsAppMessageDialog
          open={isSendWhatsAppDialogOpen}
          onClose={() => setIsSendWhatsAppDialogOpen(false)}
          appointment={messagingAppointment}
          patient={messagingPatient}
          templates={messagingTemplates}
          organizationId={organizationId}
          practitionerId={`${oids.cpf}-${user?.username}`}
          taskPatientReference={patientReferenceForTasks}
        />
      )}
    </>
  );
}
